/* T is for Tame · Round 15 offer layer (spec ~/.claude/specs/tame-shopify-horizon/round15-gamified-offers.md, P1).
   ONE engine for the cart track, the PDP unlocks row and the builder meter. Every number is computed here from the cart
   (/cart.js), the live prices the config snippet prints (snippets/tame-offers-config: settings, collections.all, live
   selling_plan_allocations) and the store's own threshold setting. No price, percent or threshold is typed in this file.
   - compute(input, cfg) is pure. The cart's Liquid twin (snippets/tame-offer-rungs) prints the same state as
     data-offers-state; qa/r15-parity.cjs diffs the two per scenario.
   - Nothing paid is ever added here. The only cart write is the per-line Subscribe chip, on a shopper's tap: ONE
     /cart/change.js (1-based line from a fresh /cart.js, selling_plan, sections), Horizon's CartLinesUpdateEvent, no reload.
   - Loaded only while a Theme settings switch (tame_offers_cart / _pdp / _build) is on (snippets/tame-tokens). */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const HAS_DOM = typeof document !== 'undefined';
const $ = (s, r) => (r || document).querySelector(s);
const $$ = (s, r) => Array.from((r || document).querySelectorAll(s));
const root = () => (typeof window !== 'undefined' && window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const readCfg = () => { try { return JSON.parse((HAS_DOM && ($('#tame-offers-config') || {}).textContent) || 'null'); } catch (e) { return null; } };
const currency = () => (typeof window !== 'undefined' && window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
export const fmt = (c) => { const n = Math.round(Number(c) || 0) / 100; try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency() }).format(n); } catch (e) { return n.toFixed(2); } };
const sub = (s, map) => String(s || '').replace(/\[(\w+)\]/g, (m, k) => (k in map ? map[k] : m));
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const rung = (cfg, id) => ((cfg && cfg.rungs) || []).find((r) => r.id === id) || { id, on: false };
const half = (c, n) => Math.floor((2 * c + n) / (2 * n)); /* c / n rounded half-up to the cent, integers only */

/* ---------- shipping: the four states of snippets/tame-cart-meter (data-ship-state), never re-derived elsewhere ---------- */
export function ship(input, cfg) {
  const s = (cfg && cfg.ship) || {};
  const thr = Number(s.threshold_cents) || 0;
  if (thr <= 0) return { state: 'none', gap_cents: 0 };
  const lines = input.lines || [];
  const ok = (s.codes_ok || []).map((c) => String(c).toUpperCase().trim());
  const otherCode = (input.codes || []).some((c) => ok.indexOf(String(c).toUpperCase().trim()) < 0);
  const hasPlan = input.sub_only != null ? !!input.sub_only || !!input.has_plan : lines.some((l) => l.plan);
  const mixed = input.sub_only != null ? !input.sub_only : lines.some((l) => !l.plan && l.line_cents > 0 && !l.kit_addon); /* kit_addon: the first kit's mini-brush, in the $0 box delivery profile (snippets/tame-sub-ship) */
  if (s.sub_only_free && hasPlan && !mixed && !otherCode) return { state: 'sub_only', gap_cents: 0 };
  /* Round 15 delta P2-19: the builder with a code in the cart (or opened by the popup's code path) cannot know the order's total after the code, so it never says it ships free (input.code_unknown) */
  if (otherCode || input.code_unknown || (hasPlan && !s.sub_only_free)) return { state: 'checkout', gap_cents: 0 };
  const total = Number(input.total_cents) || 0;
  if (thr - total > 0) return { state: 'gap', gap_cents: thr - total, fill: Math.max(0, Math.min(1, total / thr)) };
  return { state: 'free', gap_cents: 0, fill: 1 };
}

/* ---------- the pure computation ----------
   input: { context: 'cart'|'pdp'|'build', lines: [{ handle, qty, unit_cents, line_cents, plan: { cents, compare_cents }|null,
            gift, rec_cents }], codes: [], total_cents, discount_cents, swap: { ok, x, n, missing, plan }|null, build: {...} }
   returns { rungs: [{ id, state: done|locked|off, kind, gap_cents, x_cents, amount_cents, action }], lead, savings_cents, ship } */
export function compute(input, cfg) {
  const lines = input.lines || [];
  const sh = ship(input, cfg);
  const out = [];
  /* rung 1, free shipping */
  if (rung(cfg, 'ship').on !== false && sh.state !== 'none') {
    out.push({ id: 'ship', state: sh.state === 'gap' || sh.state === 'checkout' ? 'locked' : 'done', kind: sh.state, gap_cents: sh.gap_cents, fill: sh.state === 'checkout' ? 0 : sh.fill == null ? 1 : sh.fill });
  }
  /* rung 2, the FREE detangling brush with Ready, Set, Tame (only while snippets/tame-rst-brush prints yes) */
  const rb = rung(cfg, 'brush');
  const pieces = (cfg && cfg.pieces && cfg.pieces.rst) || [];
  if (rb.on && !input.blocked) {
    if (input.context === 'build') out.push(buildBrush(input.build || {}, rb, cfg, sh, input));
    else {
      const rstIn = lines.some((l) => l.handle === rb.with && !l.g_any);
      const giftFree = lines.some((l) => l.gift && l.line_cents === 0);
      const anyPiece = lines.some((l) => pieces.indexOf(l.handle) > -1 && !l.g_any);
      const sw = input.swap;
      if (rstIn) out.push({ id: 'brush', state: giftFree ? 'done' : 'off', kind: giftFree ? 'in_cart' : 'hidden' });
      else if (sw && sw.ok && !sw.plan && Number(sw.n) === 2) out.push({ id: 'brush', state: 'locked', kind: 'two', x_cents: swapX(Number(sw.x) || 0, input), missing: sw.missing || '', pieces: anyPiece, frees_ship: freesShip(sh, rb, cfg, input), action: { type: 'scroll', target: '[data-rst-swap]' } });
      else out.push({ id: 'brush', state: 'locked', kind: 'none', pieces: anyPiece, action: { type: 'link', target: rb.url || '' } });
    }
  }
  /* rung 3, Subscribe and save: the chip's eligibility (paid, one-time, not a gift, not the kit, not an accessory) */
  const nf = (cfg && cfg.nonformula) || [];
  let x = 0, done = 0;
  if (input.context === 'build') { const b = input.build || {}; done = b.sub ? Math.max(0, b.save_cents || 0) : 0; x = b.sub ? 0 : Math.max(0, b.sub_save_cents || 0); }
  else {
    lines.forEach((l) => {
      const q = Number(l.qty) || 1;
      if (l.plan) { if (l.plan.compare_cents != null && l.plan.compare_cents > l.plan.cents) done += (l.plan.compare_cents - l.plan.cents) * q; return; }
      if (l.gift || l.g_any || l.lg || !(l.line_cents > 0) || l.handle === (rb.with || '') || nf.indexOf(l.handle) > -1) return;
      if (l.rec_cents != null && l.rec_cents < l.unit_cents) x += (l.unit_cents - l.rec_cents) * q;
    });
  }
  if (rung(cfg, 'plan').on !== false) {
    if (done > 0) out.push({ id: 'plan', state: 'done', kind: 'saving', amount_cents: done });
    else if (x > 0) out.push({ id: 'plan', state: 'locked', kind: 'subscribe', amount_cents: x, action: { type: 'scroll', target: '[data-offers-subup]' } });
  }
  const shown = out.filter((r) => r.state !== 'off').slice(0, 3);
  /* one leading sentence (R18): the shipping gap, then the kit while its pieces are in the cart, then Subscribe, then the last win */
  const by = (id) => shown.find((r) => r.id === id);
  let lead = null;
  const s1 = by('ship'), s2 = by('brush'), s3 = by('plan');
  /* ruling 2026-11-03: one piece from Ready, Set, Tame leads (adding it also frees shipping); the shipping gap leads only when that case does not apply */
  if (s2 && s2.state === 'locked' && s2.kind === 'two' && s2.pieces) lead = s2;
  else if (s1 && s1.state === 'locked') lead = s1;
  else if (s2 && s2.state === 'locked' && s2.pieces) lead = s2;
  else if (s3 && s3.state === 'locked') lead = s3;
  else { const d = shown.filter((r) => r.state === 'done'); lead = d.length ? d[d.length - 1] : shown[0] || null; }
  const discount = input.context === 'build' ? 0 : Math.max(0, Number(input.discount_cents) || 0);
  const savings = Math.max(0, (input.context === 'build' ? 0 : done) + discount);
  return { rungs: out, shown, lead: lead ? { id: lead.id, state: lead.state, kind: lead.kind } : null, savings_cents: savings, plan_saving_cents: done, ship: sh };
}

/* Round 15 delta P1-16/P1-17/P2-18: "Swap for $X more" is the price AFTER an order-level percent code, exact to the cent, or no number at all.
   input.code_unknown (builder: any code in /cart.js discount_codes, applicable or not, or opened by the popup's code path): null.
   input.code_x (from codeX(cart)): kind none = the list difference; kind order = the swapped cart's total after the code minus today's total;
   kind unknown (a subscription line, a line-level code, a fixed or partial code, a code with no allocation, or numbers that do not reproduce Shopify's total): null.
   No code_x at all (the builder before /cart.js answers): the list difference. Liquid twin: snippets/tame-offer-code. */
export function swapX(x, input) {
  if (input && input.code_unknown) return null;
  const c = input && input.code_x;
  if (!c || c.kind === 'none') return Number(x);
  if (c.kind !== 'order') return null;
  return Number(x) - (Math.floor((c.s1 + Number(x)) * c.pct / 100) - c.took);
}

/* One piece from Ready, Set, Tame also closes a shipping gap when the kit's own price is at or over the threshold (ruling 2026-11-03):
   the one-away line then says both. Liquid twin: or_b_frees in snippets/tame-offer-rungs. */
function freesShip(sh, rb, cfg, input) {
  const thr = Number(((cfg && cfg.ship) || {}).threshold_cents) || 0;
  if (!(sh.state === 'gap' && thr > 0)) return false;
  /* Round 15 final check P1-12: the kit's price AFTER the cart's order-level discount codes must still reach the threshold.
     No code: list price. A code with a known percent-off-everything share (input.code_keep, from codeKeep()): scaled by it.
     A code whose effect cannot be known (code_keep null, or a code with no share): no promise. Automatic discounts (the brush BXGY)
     and selling-plan prices are not discount codes, so they never switch it off. Liquid twin: or_b_frees in snippets/tame-offer-rungs. */
  if (input && input.code_unknown) return false;
  const k = input && input.code_keep;
  if (k === null || ((input && input.codes) || []).length && typeof k !== 'number') return false;
  return (typeof k === 'number' ? Math.round(Number(rb.with_cents) * k) : Number(rb.with_cents)) >= thr;
}

/* Builder brush rung (spec P4 table). b = fromBuilder() output. */
function buildBrush(b, rb, cfg, sh, input) {
  const ids = b.ids || [];
  const R = ['spray', 'cream', 'gel'];
  const have = R.filter((k) => ids.indexOf(k) > -1);
  const piece = (k) => (b.P && b.P[k]) || {};
  const rstOnce = Number(rb.with_cents) || 0;
  const r = buildBrushKind(b, rb, have, R, piece, rstOnce, sh, cfg, input);
  r.pieces = have.length > 0;
  return r;
}
function buildBrushKind(b, rb, have, R, piece, rstOnce, sh, cfg, input) {
  if (!b.sub && have.length === 3) {
    const sum = have.reduce((a, k) => a + (piece(k).cents || 0), 0);
    return { id: 'brush', state: 'locked', kind: 'exact', x_cents: rstOnce - sum, list_cents: sum, rst_cents: rstOnce, action: { type: 'scroll', target: '[data-b-match]' } };
  }
  if (!b.sub && have.length === 2) {
    const miss = R.find((k) => have.indexOf(k) < 0);
    const sum = have.reduce((a, k) => a + (piece(k).cents || 0), 0);
    const x = rstOnce - sum;
    const adds = (piece(miss).cents || 0) + (Number(rb.gift_cents) || 0);
    if (x > 0 && x < adds) return { id: 'brush', state: 'locked', kind: 'two', x_cents: swapX(x, input), missing: piece(miss).name || '', frees_ship: freesShip(sh, rb, cfg, input), action: { type: 'link', target: rb.url || '' } };
    return { id: 'brush', state: 'locked', kind: 'none', action: { type: 'link', target: rb.url || '' } };
  }
  const rp = b.rst_plan_cents;
  if (b.sub && have.length === 3 && rp != null) {
    const kit = have.reduce((a, k) => a + ((piece(k).plans || {})[String(b.months)] || {}).cents || 0, 0);
    return { id: 'brush', state: 'locked', kind: 'sub_all', rst_plan_cents: rp, kit_cents: kit, action: { type: 'link', target: rb.url || '' } };
  }
  if (b.sub && have.length === 2 && rp != null) {
    const kit = R.reduce((a, k) => a + (((piece(k).plans || {})[String(b.months)] || {}).cents || 0), 0);
    const miss = R.find((k) => have.indexOf(k) < 0);
    return { id: 'brush', state: 'locked', kind: 'sub_two', rst_plan_cents: rp, kit_cents: kit, missing: piece(miss).name || '', action: { type: 'scroll', target: '[data-b-to-rst]' } };
  }
  return { id: 'brush', state: 'locked', kind: 'none', action: { type: 'link', target: rb.url || '' } };
}

/* ---------- adapters ---------- */
const codesOf = (cart) => {
  const c = [];
  (cart.discount_codes || []).forEach((d) => { if (d && d.code && d.applicable !== false) c.push(d.code); });
  (cart.cart_level_discount_applications || []).forEach((a) => { if (a && a.type === 'discount_code') c.push(a.title); });
  (cart.items || []).forEach((it) => (it.line_level_discount_allocations || []).forEach((d) => { const a = d.discount_application || {}; if (a.type === 'discount_code') c.push(a.title); }));
  return Array.from(new Set(c.map((x) => String(x).toUpperCase().trim())));
};
/* The share of the order that discount CODES leave behind: total / (total + what the codes took), only when every code is a percent-off on the whole order.
   cart.js fields: discount_application { type: 'discount_code' | 'automatic' | 'manual', value_type, target_selection, total_allocated_amount } in
   cart_level_discount_applications and in each line's line_level_discount_allocations. total_discount also holds automatic discounts, so it is not used.
   keep is 1 with no code, a number when known, null when it cannot be known. Liquid twin: cart.discount_applications in snippets/tame-offer-rungs. */
export function codeKeep(cart) {
  const codes = codesOf(cart || {});
  if (!codes.length) return { codes, keep: 1 };
  const apps = {};
  const add = (a) => { if (a && a.type === 'discount_code') apps[String(a.title).toUpperCase().trim()] = a; };
  ((cart && cart.cart_level_discount_applications) || []).forEach(add);
  ((cart && cart.items) || []).forEach((it) => (it.line_level_discount_allocations || []).forEach((d) => add(d.discount_application)));
  const list = Object.keys(apps).map((k) => apps[k]);
  const took = list.reduce((a, x) => a + (Number(x.total_allocated_amount) || 0), 0);
  const plain = list.length >= codes.length && list.every((x) => x.value_type === 'percentage' && x.target_selection === 'all');
  const total = Number(cart.total_price) || 0;
  return { codes, keep: plain && took > 0 && total > 0 ? total / (total + took) : null };
}
/* Every code the cart lists, applicable or not (cart.js discount_codes: [{ code, applicable }]), plus every code application on the cart or a line. */
export function codesListed(cart) {
  const c = ((cart && cart.discount_codes) || []).filter((d) => d && d.code).map((d) => String(d.code).toUpperCase().trim());
  return Array.from(new Set(c.concat(codesOf(cart || {}))));
}
/* The exact reader behind swapX. Shopify takes floor(subtotal x percent) off the discountable lines for an order-level percent code
   (real reads: $4.04 on $26.98 and $6.14 on $40.98, both the floor of the whole subtotal, while per-line floors would give $4.03), so after a swap
   the cart pays (S1 + X) - floor((S1 + X) x pct / 100) against S1 - took today. S1 = the sum of the lines (cart.js final_line_price carries no order-level code).
   The percent is read back from took / S1 and must reproduce took, or the answer is unknown. Liquid twin: snippets/tame-offer-code. */
export function codeX(cart) {
  const codes = codesOf(cart || {}); /* applicable codes only: a listed code that applies to nothing takes nothing (Liquid sees applications only) */
  if (!codes.length) return { kind: 'none' };
  const items = (cart && cart.items) || [];
  const apps = (cart && cart.cart_level_discount_applications) || [];
  const s1 = items.reduce((a, it) => a + (Number(it.final_line_price) || 0), 0);
  const lineCode = items.some((it) => (it.line_level_discount_allocations || []).some((d) => (d.discount_application || {}).type === 'discount_code'));
  const plan = items.some((it) => it.selling_plan_allocation);
  const mine = apps.filter((a) => a && a.type === 'discount_code');
  const took = mine.reduce((a, x) => a + (Number(x.total_allocated_amount) || 0), 0);
  const plain = mine.length === 1 && codes.length === 1 && mine[0].value_type === 'percentage' && mine[0].target_selection === 'all';
  if (lineCode || plan || !plain || !(took > 0) || !(s1 > 0)) return { kind: 'unknown' };
  const pct = half(took * 100, s1);
  return Math.floor(s1 * pct / 100) === took ? { kind: 'order', pct, took, s1 } : { kind: 'unknown' };
}
/* The Daily kit's first-box mini brush ships free with the box ($0 delivery profile, snippets/tame-sub-ship). It counts as part of the
   subscription ONLY when its handle is exactly the kit's add-on, it shares a _routine key with a subscribed line, AND every subscribed line
   in the order is the Daily kit box (staff re-review 2026-11-03 P1-8). Any other mix stays "mixed". One helper for the cart and the PDP. */
const KIT_BOX = 'daily-routine-box', KIT_ADDON = 'daily-routine-first-box-brush';
export function markKit(lines, cfg) {
  const k = (cfg && cfg.kit) || {};
  const box = k.box || KIT_BOX, addon = k.addon || KIT_ADDON;
  const plan = lines.filter((l) => l.plan);
  const onlyBox = plan.length > 0 && plan.every((l) => l.handle === box);
  const keys = plan.filter((l) => l.routine).map((l) => l.routine);
  lines.forEach((l) => { l.kit_addon = !!(onlyBox && !l.plan && l.handle === addon && l.routine && keys.indexOf(l.routine) > -1); });
  return lines;
}
/* plans: optional { variant_id: recommended plan cents } (the PDP knows its own product; the parity test reads /products/<h>.js) */
export function fromCart(cart, cfg, extra) {
  const rb = rung(cfg, 'brush');
  const ex = extra || {};
  const lines = (cart.items || []).map((it) => {
    const spa = it.selling_plan_allocation;
    const props = it.properties || {};
    return {
      handle: it.handle, vid: it.variant_id, key: it.key, qty: it.quantity,
      unit_cents: it.original_price, line_cents: it.final_line_price,
      plan: spa ? { cents: spa.price, compare_cents: spa.compare_at_price == null ? null : spa.compare_at_price } : null,
      routine: props._routine ? String(props._routine) : '',
      gift: props._gift === 'rst', g_any: !!props._gift, lg: !!props._ladder_gift,
      rec_cents: ex.plans && ex.plans[it.variant_id] != null ? ex.plans[it.variant_id] : null,
    };
  });
  markKit(lines, cfg);
  /* a cart the brush reconciler marked rst_gift_blocked never gets the brush (tame.js), so no rung or chip promises it (staff P1-4) */
  return { context: ex.context || 'cart', lines, codes: codesOf(cart), code_keep: codeKeep(cart).keep, code_x: codeX(cart), total_cents: cart.total_price, discount_cents: cart.total_discount, swap: ex.swap || null, blocked: ((cart.attributes || {}).rst_gift_blocked || '') === '1' };
}
/* the current cart plus this selection (sel: { lines: [{ handle, qty, unit_cents, line_cents, plan, rec_cents }] }) */
export function fromPdp(cart, cfg, sel) {
  const base = fromCart(cart || { items: [], total_price: 0, total_discount: 0 }, cfg, { context: 'pdp' });
  const add = (sel && sel.lines) || [];
  base.lines = markKit(base.lines.concat(add), cfg);
  base.total_cents = (Number(base.total_cents) || 0) + add.reduce((a, l) => a + (l.line_cents || 0), 0);
  return base;
}
/* the builder's one unreadable-code rule: ?pv=B (the popup's code path) counts as "a code may be present" only until /cart.js answers; after that discount_codes decides (any listed code, applicable or not) */
/* P2-27: the discount_code cookie (set by /discount/CODE, not HttpOnly) is the same signal as pv=B for shoppers who got the code from popup arm A, the inline band or a partner link */
export function codeHint(search, cookie) {
  return /[?&]pv=B(&|$)/.test(search || '') || /(?:^|;\s*)discount_code=[^;]/.test(cookie || '');
}
export function buildCodeUnknown(cart, search, cookie) {
  return cart ? codesListed(cart).length > 0 : codeHint(search, cookie);
}
/* P2-28: only a real /cart.js answer counts: r.ok and discount_codes an array */
export function validCartAnswer(ok, c) {
  return !!ok && !!c && typeof c === 'object' && Array.isArray(c.discount_codes);
}
/* the builder's own quote() output: q (current mode), qSub (quote(ids, true)), ids, sub, days; P and rstPlan come from TAME_CAT */
export function fromBuilder(d, cfg) {
  const q = d.q || {}, qs = d.qSub || {};
  const MONTHS = { 61: 2, 91: 3, 122: 4 };
  const months = MONTHS[d.days] || 2;
  /* P1-16: popup arm B applies TAMEHELLO15 to an often EMPTY cart, so no application shows. Any listed code (applicable or not) or the popup's code path
     (?pv=B on the builder URL, d.popup, counts only until /cart.js answers) means the build's price after the code is unreadable: no X, no free-shipping half, the brush half stays. */
  const listed = d.cart ? codesListed(d.cart) : [];
  const unknown = listed.length > 0 || (!!d.popup && !d.cart); /* pv=B only holds the line until /cart.js answers; after that discount_codes decides */
  return {
    context: 'build', lines: [], codes: [], code_unknown: unknown, cart_read: !!d.cart, code_keep: unknown ? null : d.cart ? codeKeep(d.cart).keep : null, total_cents: q.today || 0, discount_cents: 0,
    sub_only: !!(q.sub && !q.mixed), has_plan: !!q.sub,
    build: { ids: d.ids || [], sub: !!q.sub, months, P: d.P || {}, rst_plan_cents: d.rstPlan != null ? d.rstPlan : null, save_cents: q.sub ? (q.list || 0) - (q.today || 0) : 0, sub_save_cents: qs.sub ? (qs.list || 0) - (qs.today || 0) : 0, n: (d.ids || []).length },
  };
}

/* ---------- words (P5); the client's own lines come from the config (settings) ---------- */
export function words(r, cfg, ctx) {
  const rb = rung(cfg, 'brush');
  const c = (cfg && cfg.copy) || {};
  const tc = (cfg && cfg.ship && cfg.ship.threshold_cents) || 0;
  const thr = tc % 100 === 0 ? fmt(tc).replace(/[.,]00$/, '') : fmt(tc); /* "at $35", as the cart's money_without_trailing_zeros prints it */
  const out = { label: '', sub: '', sentence: '', short: '' };
  if (r.id === 'ship') {
    out.label = c.ship_label || 'Free shipping'; out.sub = 'at ' + thr;
    if (r.kind === 'gap') { out.sentence = sub(c.ship_todo, { amount: fmt(r.gap_cents), threshold: thr }); out.short = sub(c.short_ship, { amount: fmt(r.gap_cents) }); }
    else if (r.kind === 'checkout') { out.sentence = 'Shipping is calculated at checkout.'; out.short = out.sentence; }
    else if (r.kind === 'sub_only') { out.sentence = 'Subscription-only orders ship free in the US.'; out.short = 'This order ships free.'; }
    else { out.sentence = sub(c.ship_done, { threshold: thr }); out.short = 'This order ships free.'; }
  } else if (r.id === 'brush') {
    out.label = c.brush_label || 'FREE detangling brush'; out.sub = 'with Ready, Set, Tame';
    const rst = fmt(rb.with_cents);
    if (r.kind === 'in_cart') { out.sentence = 'Your detangling brush is free on this order. It is in your cart.'; out.short = 'Your detangling brush is free.'; }
    else if (r.kind === 'two' && ctx === 'build') { out.sentence = r.x_cents == null ? `One piece from Ready, Set, Tame: add the ${r.missing} and get the FREE detangling brush.` : `One piece from Ready, Set, Tame: ${fmt(r.x_cents)} more adds the ${r.missing} and the FREE detangling brush${r.frees_ship ? ' and free shipping' : ''}.`; out.short = r.x_cents == null ? 'Swap to Ready, Set, Tame' : `${fmt(r.x_cents)} to Ready, Set, Tame`; }
    else if (r.kind === 'two') { out.sentence = r.x_cents == null ? 'One piece from Ready, Set, Tame. Swap and get the FREE detangling brush.' : r.frees_ship ? `One piece from Ready, Set, Tame. ${fmt(r.x_cents)} more: FREE brush and free shipping.` : `One piece from Ready, Set, Tame. Swap for ${fmt(r.x_cents)} more and get the FREE detangling brush.`; out.short = r.x_cents == null ? 'Swap to Ready, Set, Tame' : `${fmt(r.x_cents)} to Ready, Set, Tame`; }
    else if (r.kind === 'exact') { out.sentence = `That is Ready, Set, Tame: ${fmt(r.rst_cents)} instead of ${fmt(r.list_cents)}, and the FREE detangling brush.`; out.short = `Ready, Set, Tame · ${fmt(r.rst_cents)}`; }
    else if (r.kind === 'sub_two') { out.sentence = `With the ${r.missing} on Subscribe, your 3 pieces renew at ${fmt(r.kit_cents)}. Ready, Set, Tame renews at ${fmt(r.rst_plan_cents)} and adds the FREE detangling brush.`; out.short = `Ready, Set, Tame · ${fmt(r.rst_plan_cents)}`; }
    else if (r.kind === 'sub_all') { out.sentence = `FREE detangling brush comes with Ready, Set, Tame (renews at ${fmt(r.rst_plan_cents)})`; out.short = `Ready, Set, Tame · ${fmt(r.rst_plan_cents)}`; }
    else { out.sentence = rb.line || ''; out.short = `Ready, Set, Tame · ${rst}`; }
  } else if (r.id === 'plan') {
    out.label = 'Subscribe';
    if (r.state === 'done') { out.sub = 'You save ' + fmt(r.amount_cents); out.sentence = `You save ${fmt(r.amount_cents)} a delivery.`; out.short = out.sentence; }
    else { out.sub = 'save ' + fmt(r.amount_cents) + ' a delivery'; out.sentence = `Subscribe and save ${fmt(r.amount_cents)} a delivery.`; out.short = `Save ${fmt(r.amount_cents)} a delivery`; }
  }
  return out;
}

/* ---------- renderers ---------- */
const ICON = { ship: 'i-truck', brush: 'i-brush', plan: 'i-calendar' };
const svg = (id, cls) => `<svg class="i${cls ? ' ' + cls : ''}" aria-hidden="true" focusable="false"><use href="#${id}"/></svg>`;
/* P2-29: renderTrack rebuilds the track's links ("See Ready, Set, Tame"); if one of them held focus, the new copy (same href) gets it. Only a focused link inside the host counts. */
function keepFocus(host, fn) {
  const a = document.activeElement, href = a && a !== document.body && host && host.contains(a) && a.tagName === 'A' ? a.getAttribute('href') : null;
  fn();
  if (href && !a.isConnected) { try { const n = host.querySelector('a[href="' + CSS.escape(href) + '"]'); if (n) n.focus({ preventScroll: true }); } catch (e) { /* no counterpart */ } }
}
/* the builder track (the cart's twin is server-rendered by snippets/tame-offer-rungs with the same classes) */
export function renderTrack(el, st, cfg) {
  if (!el) return;
  const rs = st.shown;
  if (!rs.length) { el.hidden = true; el.innerHTML = ''; return; }
  const lead = st.lead ? rs.find((r) => r.id === st.lead.id) : null;
  const lw = lead ? words(lead, cfg, 'build') : { sentence: '' };
  const rb = rung(cfg, 'brush');
  const segs = rs.map((r) => `<span class="tame-offers__seg${r.state === 'done' ? ' is-done' : ''}"><i style="width:${Math.round((r.id === 'ship' ? (r.fill || 0) : r.state === 'done' ? 1 : 0) * 100)}%"></i></span>`).join('');
  const nodes = rs.map((r) => {
    const w = words(r, cfg, 'build');
    const sub2 = r.id === 'brush' && rb.gift_cents ? `<s>${fmt(rb.gift_cents)}</s> ${esc(w.sub)}${r.kind === 'none' ? ' · ' + fmt(rb.with_cents) : ''}` : esc(w.sub);
    const inner = `<span class="tame-offers__dot">${svg(r.state === 'done' ? 'i-check' : ICON[r.id])}</span><b>${esc(w.label)}</b><span>${sub2}</span>`;
    const link = r.state === 'locked' && r.action && r.action.type === 'link' && r.action.target;
    return `<li class="tame-offers__node${r.state === 'done' ? ' is-done' : ''}" data-rung="${r.id}" data-state="${r.state}">${link ? `<a class="tame-offers__act" href="${esc(r.action.target)}">${inner}</a>` : inner}</li>`;
  }).join('');
  el.hidden = false;
  el.dataset.offersState = JSON.stringify(stateJson(st));
  el.innerHTML = `<p class="tame-offers__lead">${svg(lead && lead.state === 'done' ? 'i-check' : lead ? ICON[lead.id] : 'i-truck')}<span>${esc(lw.sentence)}</span></p><div class="tame-offers__bar" aria-hidden="true">${segs}</div><ol class="tame-offers__nodes tame-offers__nodes--${rs.length}">${nodes}</ol>`;
}
/* the builder's four named steps; the "N of 4 steps picked" text beside it stays the live region */
export function renderMeter(el, steps) {
  if (!el) return;
  const N = [['i-wash', 'Wash'], ['i-knots', 'Detangle'], ['i-smooth', 'Smooth or style'], ['i-finish', 'Finish']];
  el.hidden = false;
  el.innerHTML = `<ol class="tame-offers__steps" aria-hidden="true">${N.map(([ic, nm], i) => `<li class="${steps[i] ? 'is-done' : ''}"><span class="tame-offers__sdot">${svg(steps[i] ? 'i-check' : ic)}</span><span>${nm}</span></li>`).join('')}</ol>`;
}
export const stateJson = (st) => ({
  rungs: st.shown.map((r) => ({ id: r.id, state: r.state, kind: r.kind, cents: r.gap_cents || r.x_cents || r.amount_cents || 0 })),
  lead: st.lead ? st.lead.id + ':' + st.lead.state : '', savings_cents: st.savings_cents, ship: st.ship.state, ship_gap: st.ship.gap_cents || 0,
});

/* ---------- browser wiring ---------- */
const CFG = HAS_DOM ? readCfg() : null;
const reduce = HAS_DOM && ((window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) || document.body.classList.contains('tame-still'));
const getCart = async () => (await fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })).json();
const ss = { get(k) { try { return sessionStorage.getItem(k); } catch (e) { return null; } }, set(k, v) { try { sessionStorage.setItem(k, v); } catch (e) { /* ignore */ } } };

/* One calm moment per rung per session (R22): a rung that turns done after a cart change gets a 1.4s tint, once. Rungs
   already done when the page loads are marked seen, so a reload never replays it. Off under reduced motion and tame-still. */
function moments(first) {
  $$('[data-offers-state] [data-rung]').forEach((n) => {
    const k = 'tame_offers_seen_' + n.dataset.rung;
    if (n.dataset.state !== 'done') return;
    if (ss.get(k)) return;
    ss.set(k, '1');
    if (first || reduce) return;
    n.classList.add('is-moment');
    setTimeout(() => n.classList.remove('is-moment'), 1400);
  });
}

/* Per-line Subscribe chip (cart): the kitToPlan pattern (assets/tame.js), ONE /cart/change.js */
async function lineToPlan(btn) {
  const key = btn.dataset.key, plan = Number(btn.dataset.plan);
  const sectionIds = $$('cart-items-component').map((c) => c.dataset.sectionId).filter(Boolean);
  const note = btn.closest('.tame-subup') && $('[data-subup-err]', btn.closest('.tame-subup'));
  failedKeys.delete(key);
  if (btn.disabled) return; /* a double tap is one change */
  btn.disabled = true; btn.setAttribute('aria-busy', 'true');
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(new CartLinesUpdateEvent({ action: 'update', context: 'tame-subup', lines: [], promise: deferred.promise }));
  try {
    const cart = await getCart();
    const i = (cart.items || []).findIndex((it) => it.key === key && !it.selling_plan_allocation);
    if (i < 0 || !plan) throw new Error('line not found');
    const it = cart.items[i];
    const props = Object.assign({}, it.properties || {}, { _tame_subup: '1' });
    const r = await fetch(root() + 'cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ line: i + 1, quantity: it.quantity, selling_plan: plan, properties: props, sections: sectionIds.join(',') }) });
    const d = await r.json();
    if (!r.ok || d.status) throw new Error(d.description || d.message || 'change failed');
    deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(d), detail: { items: d.items, source: 'tame-subup', sections: d.sections, didError: false } });
  } catch (e) {
    deferred.resolve({ cart: null, detail: { didError: true } });
    btn.disabled = false; btn.removeAttribute('aria-busy');
    if (note) note.hidden = false;
    /* Horizon re-renders the drawer after an errored update and restores the hidden notice: put it back after each paint until the shopper acts again (QA not-tested: failure notice) */
    failedKeys.add(key);
    [0, 120, 400, 1000].forEach((ms) => setTimeout(showFailed, ms));
  }
}
const failedKeys = new Set();
function showFailed() {
  failedKeys.forEach((k) => {
    const b = $('[data-offers-subup][data-key="' + k + '"]');
    const n = b && b.closest('.tame-subup') && $('[data-subup-err]', b.closest('.tame-subup'));
    if (n && n.hidden) n.hidden = false;
    if (b && b.disabled) { b.disabled = false; b.removeAttribute('aria-busy'); }
  });
}

/* ---------- PDP: the unlocks row (snippets/tame-unlocks) ---------- */
export function pdpSelection(row, data) {
  const box = $('[data-tame-buy]');
  const mode = box ? box.dataset.mode : 'up';
  const form = $('form[data-type="add-to-cart-form"]');
  const vid = Number(((form && $('input[name="id"]', form)) || {}).value) || data.vid;
  const qty = Math.max(1, Number(((form && $('input[name="quantity"]', form)) || {}).value) || 1);
  const v = (data.variants || []).find((x) => x.id === vid) || (data.variants || [])[0] || {};
  if (mode === 'box') {
    const po = ((box && $('input[data-po]:checked', box)) || {}).value || 'sub';
    if (po === 'sub' && data.box) {
      const m = String(((box && $('input[data-cad]:checked', box)) || {}).value || data.box.def || '2');
      const b = data.box.m[m] || data.box.m[data.box.def] || {};
      return { po: 'sub', box: true, months: m, v, qty: 1, lines: [{ handle: data.box.handle, routine: 'pdp', qty: 1, unit_cents: b.list || b.refill, line_cents: b.refill, plan: { cents: b.refill, compare_cents: b.list || null }, gift: false, rec_cents: null }, { handle: data.box.addon || KIT_ADDON, routine: 'pdp', qty: 1, unit_cents: b.first - b.refill, line_cents: b.first - b.refill, plan: null, gift: false, rec_cents: null }].filter((l) => !(l.plan == null && !(l.line_cents > 0))) };
    }
    if (po === 'sub') { const pl = v.rec; return { po: 'sub', box: true, v, qty: 1, lines: pl ? [{ handle: data.handle, qty: 1, unit_cents: v.cents, line_cents: pl.cents, plan: { cents: pl.cents, compare_cents: null }, gift: false, rec_cents: null }] : [] }; } /* a box buy without Daily-kit data (the kit's own box): its plan line (staff P0) */
    return { po: 'one', box: true, v, qty, lines: [{ handle: data.handle, qty, unit_cents: v.cents, line_cents: v.cents * qty, plan: null, gift: false, rec_cents: v.rec ? v.rec.cents : null }] };
  }
  /* an upgrade picked in the single's buy box (Wash Night, the kit, a duo): that offer has its own button and price, so the row says nothing (staff P1-3) */
  const upPo = ((box && $('input[data-po]:checked', box)) || {}).value || 'one';
  if (upPo !== 'one') return { po: upPo, upgrade: true, v, qty, lines: [] };
  const sp = box && $('[data-sub-pick]', box);
  const isSub = !!(sp && !sp.closest('[hidden]') && (($('input[data-sp-mode]:checked', sp) || {}).value === 'sub'));
  const cadId = isSub ? Number((($('input[data-sp-cad]:checked', sp) || {}).value) || 0) : 0;
  const pl = isSub ? ((v.plans || []).find((p) => p.id === cadId) || v.rec) : null;
  return { po: isSub ? 'sub' : 'one', v, qty, lines: [{ handle: data.handle, qty, unit_cents: v.cents, line_cents: (pl ? pl.cents : v.cents) * qty, plan: pl ? { cents: pl.cents, compare_cents: pl.compare } : null, gift: false, rec_cents: v.rec ? v.rec.cents : null }] };
}
/* Does the picked option get the brush (chip and sticker)? Subscribe only while tame_offer_brush_on_sub is on (rb.on_sub); never in a blocked cart. Pure, unit-tested. */
export function brushOnFor(data, rb, cart, sel) {
  return !!(data && data.rst && rb && rb.on && !(cart && ((cart.attributes || {}).rst_gift_blocked || '') === '1') && (sel.po !== 'sub' || rb.on_sub));
}
function chip(id, icon, html, done, extra) {
  return `<li class="tame-unl__chip${done ? ' is-done' : ''}" data-chip="${id}"${extra || ''}>${svg(done ? 'i-check' : icon)}<span>${html}</span></li>`;
}
function renderUnlocks(row, cart) {
  const data = JSON.parse(($('[data-unl-data]', row) || {}).textContent || '{}');
  if (!data.handle) return;
  const cfg = CFG;
  const sel = pdpSelection(row, data);
  if (sel.upgrade) { const ul0 = $('[data-unl-list]', row); if (ul0.innerHTML) ul0.innerHTML = ''; const su0 = $('[data-unl-stepup]', row); if (su0) su0.hidden = true; if (data.rst) sticker(false); row.classList.add('is-ready'); return; }
  const st = compute(fromPdp(cart, cfg, sel), cfg);
  const rb = rung(cfg, 'brush');
  const list = [];
  /* shipping chip: (cart + this), the cart's own rule; hidden while another code is on the cart */
  const s = st.ship;
  if (s.state === 'gap') list.push(chip('ship', 'i-truck', `${esc(fmt(s.gap_cents))} to free shipping`, false));
  else if (s.state === 'free') list.push(chip('ship', 'i-truck', 'Free shipping', true));
  else if (s.state === 'sub_only') list.push(chip('ship', 'i-truck', 'Ships free', true));
  /* brush chip: the kit's page only, on every option the reconciler gives the brush to */
  const brushOn = brushOnFor(data, rb, cart, sel);
  if (brushOn) list.push(chip('brush', 'i-brush', `FREE detangling brush <s>${esc(fmt(rb.gift_cents))}</s>`, true));
  const v = sel.v || {};
  /* pack chip: a 2-count variant, per-unit price rounded half-up, the saving computed from the totals */
  const two = (data.variants || []).find((x) => x.count === 2), one = (data.variants || []).find((x) => x.count === 1);
  if (two && one && !sel.box) {
    const sub2 = sel.po === 'sub';
    const p2 = sub2 ? (two.rec ? two.rec.cents : null) : two.cents, p1 = sub2 ? (one.rec ? one.rec.cents : null) : one.cents;
    if (p2 != null && p1 != null && 2 * p1 - p2 > 0) {
      const on2 = v.id === two.id;
      list.push(chip('pack', 'i-tag', on2 ? `${esc(fmt(half(p2, 2)))} each, ${esc(fmt(2 * p1 - p2))} less than two singles` : `2 count: ${esc(fmt(half(p2, 2)))} each`, on2));
    }
  }
  /* plan chip (last, so the two short chips share a row on phones): One-time selected on a product whose recommended plan saves; a tap picks Subscribe, never adds */
  if (!data.rst && !sel.box && sel.po === 'one' && v.rec && v.rec.cents < v.cents) {
    list.push(`<li class="tame-unl__chip tame-unl__chip--act" data-chip="plan"><button type="button" data-unl-sub>${svg('i-calendar')}<span>Subscribe and save ${esc(fmt((v.cents - v.rec.cents) * sel.qty))} a delivery</span></button></li>`);
  }
  const ul = $('[data-unl-list]', row);
  /* at most 3 chips, in this order of importance when the row is too long: shipping, Subscribe and save, brush, 2-count. One line at 1280
     (990px and up), at most 2 lines on a phone (QA P2 c): the least important chip is dropped until the row fits.
     The fit is measured in a hidden copy of the list, so the live region (aria-live) is touched once, and only for chips that changed
     (final check P2-15): a repaint with the same chips announces nothing. */
  const rank = { ship: 0, plan: 1, brush: 2, pack: 3 };
  let keep = list.slice(0, 3);
  const idOf = (h) => (h.match(/data-chip="(\w+)"/) || [])[1];
  const maxLines = HAS_DOM && window.matchMedia && window.matchMedia('(min-width: 990px)').matches ? 1 : 2;
  const probe = ul.cloneNode(false);
  probe.removeAttribute('data-unl-list'); probe.removeAttribute('aria-live'); probe.removeAttribute('role'); probe.setAttribute('aria-hidden', 'true');
  probe.style.cssText = 'position:absolute;visibility:hidden;pointer-events:none;left:0;top:0;width:' + (ul.getBoundingClientRect().width || ul.parentNode.getBoundingClientRect().width) + 'px'; /* an empty list is display:none (width 0): fall back to its parent */
  ul.parentNode.insertBefore(probe, ul.nextSibling);
  const lines = () => { const tops = new Set(); $$('.tame-unl__chip', probe).forEach((c) => tops.add(c.offsetTop)); return tops.size; };
  probe.innerHTML = keep.join('');
  while (keep.length > 1 && lines() > maxLines) {
    const worst = keep.reduce((a, h) => (rank[idOf(h)] > rank[idOf(a)] ? h : a), keep[0]);
    keep = keep.filter((h) => h !== worst);
    probe.innerHTML = keep.join('');
  }
  const fin = $$('.tame-unl__chip', probe).map((n) => ({ id: n.getAttribute('data-chip'), html: n.outerHTML }));
  probe.remove();
  const ops = chipOps($$('.tame-unl__chip', ul).map((n) => ({ id: n.getAttribute('data-chip'), html: n.outerHTML })), fin);
  const tpl = document.createElement('template');
  ops.forEach((o, i) => {
    const have = $$('.tame-unl__chip', ul).find((n) => n.getAttribute('data-chip') === o.id);
    let node = have;
    if (!have || !o.same) { tpl.innerHTML = o.html; node = tpl.content.firstElementChild; if (have) have.replaceWith(node); }
    if (ul.children[i] !== node) ul.insertBefore(node, ul.children[i] || null);
  });
  $$('.tame-unl__chip', ul).forEach((n) => { if (!ops.some((o) => o.id === n.getAttribute('data-chip'))) n.remove(); });
  /* Spray Duo pages, Subscribe: the RST subscription step-up, shown only when both R6 checks pass at this cadence */
  const su = $('[data-unl-stepup]', row);
  if (su) {
    const t = stepupText(data, sel, rb);
    su.hidden = !t; su.textContent = t || '';
  }
  /* RST sticker: only while the picked option gets the brush, removed in the same frame otherwise */
  if (data.rst) sticker(brushOn);
  row.dataset.offersState = JSON.stringify(stateJson(st));
  row.classList.add('is-ready');
}
/* P2-15: which chips of the live list actually changed. cur and want are [{ id, html }] in display order (html as the DOM serialises it);
   a wanted chip is `same` when the list already holds a chip with that id and identical html, so it is left alone and not announced again. */
export function chipOps(cur, want) {
  const had = {};
  (cur || []).forEach((c) => { had[c.id] = c.html; });
  return (want || []).map((w) => ({ id: w.id, html: w.html, same: had[w.id] === w.html }));
}
export function stepup(su, months, brushOnSub) {
  if (!su || !su.m) return null;
  const b = su.m[String(months)];
  if (!b || !(b.rst > 0) || !(b.first > 0) || !(b.refill > 0)) return null;
  const addon = b.first - b.refill;
  const xf = b.rst - b.first, xr = b.rst - b.refill;
  const addFirst = su.piece_cents + (brushOnSub ? su.brush_cents : 0) - addon;
  const addRenew = su.piece_cents;
  const ok = xf > 0 && xr > 0 && xf < addFirst && xr < addRenew && xf <= b.first && xr <= b.refill;
  return { ok, xf, xr, addFirst, addRenew, months: Number(months) };
}
function stepupText(data, sel, rb) {
  if (!data.stepup || !sel.box || sel.po !== 'sub') return '';
  const r = stepup(data.stepup, sel.months, rb.on && rb.on_sub);
  if (!r || !r.ok) return '';
  return `${fmt(r.xf)} more today, then ${fmt(r.xr)} more every ${r.months} months gets you the Ready, Set, Tame subscription, which adds the ${data.stepup.piece}${rb.on && rb.on_sub ? ' and the FREE detangling brush in the first order' : ''}.`;
}
function sticker(on) {
  const stage = $('.pdp__media .pdp__stage');
  let st = $('[data-offers-sticker]');
  if (!on) { if (st) st.remove(); return; }
  if (!stage || st) return;
  st = document.createElement('span');
  st.className = 'tame-sticker'; st.dataset.offersSticker = '';
  st.innerHTML = `${svg('i-brush')}<span>FREE detangling brush</span>`;
  stage.appendChild(st);
}

/* ---------- wiring (only the switches that are on) ---------- */
if (HAS_DOM && CFG && CFG.on) {
  window.TameOffers = { compute, ship: (i) => ship(i, CFG), fromCart, fromPdp, fromBuilder, words, stateJson, stepup, cfg: CFG };
  if (CFG.on.cart) {
    moments(true);
    document.addEventListener(StandardEvents.cartLinesUpdate, (ev) => { (ev.promise || Promise.resolve()).then(() => setTimeout(() => { moments(false); showFailed(); }, 120)).catch(() => {}); });
    document.addEventListener('click', (e) => {
      const b = e.target.closest && e.target.closest('[data-offers-subup]');
      if (b) { e.preventDefault(); lineToPlan(b); return; }
      const a = e.target.closest && e.target.closest('[data-offers-go]');
      if (a) { const t = $(a.dataset.offersGo); if (t) { e.preventDefault(); t.scrollIntoView({ block: 'nearest', inline: 'start', behavior: reduce ? 'auto' : 'smooth' }); const f = t.matches('button, a, [tabindex]') ? t : t.querySelector('button, a'); if (f) f.focus({ preventScroll: true }); } }
    });
  }
  if (CFG.on.pdp) {
    const rows = $$('[data-tame-unlocks]');
    if (rows.length) {
      let cart = null;
      const paint = () => rows.forEach((r) => { try { renderUnlocks(r, cart); } catch (e) { /* the row is a bonus; the buy box never depends on it */ } });
      const load = () => getCart().then((c) => { cart = c; paint(); }).catch(() => paint());
      load();
      document.addEventListener('change', (e) => { const t = e.target; if (t && t.matches && t.matches('input[data-po], input[data-sp-mode], input[data-sp-cad], input[data-cad], input[name="quantity"]')) setTimeout(paint, 0); });
      document.addEventListener(StandardEvents.cartLinesUpdate, (ev) => { (ev.promise || Promise.resolve()).then(() => load()).catch(() => {}); });
      /* a size switch: tame.js copies the [data-tame-sync] HTML inside the productSelect promise; repaint right after it, in the same task chain */
      document.addEventListener(StandardEvents.productSelect, (ev) => { if (ev.promise) ev.promise.then(() => setTimeout(paint, 0)).catch(() => {}); });
      document.addEventListener('click', (e) => {
        const b = e.target.closest && e.target.closest('[data-unl-sub]');
        if (!b) return;
        const sp = $('[data-tame-buy] [data-sub-pick]'); const r = sp && $('input[data-sp-mode][value="sub"]', sp);
        if (r) { r.checked = true; r.dispatchEvent(new Event('change', { bubbles: true })); r.focus({ preventScroll: true }); }
      });
    }
  }
  if (CFG.on.build) {
    const builderCart = { cart: null, at: 0, paint: null };
    /* P2-23: the ONE unreadable-code rule of the builder track (fromBuilder: popup path or any listed code), exported so tame-v3.js's Shipping row reads the same answer */
    const builderCodeUnknown = () => buildCodeUnknown(builderCart.cart, window.location.search, document.cookie);
    window.TameOffers.codeUnknown = builderCodeUnknown;
    document.addEventListener('tame:build', (e) => {
      const host = $('[data-offers-track="build"]');
      if (!host) return;
      /* the builder's own catalogue (snippets/tame-v3-cat, live collections.all): piece prices, plans, the kit's plan at this cadence */
      const CAT = window.TAME_CAT || {};
      const MONTHS = { 61: 2, 91: 3, 122: 4 };
      const rst = (CAT.boxes && CAT.boxes.rst) || null;
      const rp = rst && rst.vid && rst.plans ? rst.plans[String(MONTHS[(e.detail || {}).days] || 2)] : null;
      const d = Object.assign({}, e.detail || {}, { P: CAT.products || {}, rstPlan: rp ? rp.cents : null, popup: codeHint(window.location.search, document.cookie) });
      const P = d.P || {};
      const paint = (cart) => {
        d.cart = cart;
        keepFocus(host, () => renderTrack(host, compute(fromBuilder(d, CFG), CFG), CFG));
      };
      /* the cart's discount codes (popup arm B applies TAMEHELLO15, then opens the builder) decide whether the kit line may promise free shipping;
         until /cart.js answers, or if it fails, the shipping half of the sentence is left out (P1-12) */
      /* the answer paints with the LATEST render's numbers (builderCart.paint), not the render that asked: a slow /cart.js used to repaint the page-load state over the picks (QA 2026-11-03) */
      builderCart.paint = paint;
      paint(builderCart.cart);
      if (Date.now() - builderCart.at > 30000) {
        builderCart.at = Date.now();
        fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then((r) => r.json().then((c) => { if (!validCartAnswer(r.ok, c)) throw new Error('cart.js not usable'); return c; })).then((c) => { const was = builderCodeUnknown(); builderCart.cart = c; if (builderCart.paint) builderCart.paint(c); if (was !== builderCodeUnknown()) document.dispatchEvent(new CustomEvent('tame:build-code')); }).catch(() => {});
      }
      renderMeter($('[data-offers-meter]'), [1, 2, 3, 4].map((s) => (d.ids || []).some((id) => P[id] && P[id].step === s)));
    });
  }
}
